// Libs
import classNames from 'classnames/bind';
import { FormControlLabel, Grid2, IconButton, Radio } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
import { BaseInput, BaseInputAddress } from '~/components/common';
// Others
import { IFormCustomer } from '~/utils/interface/customer';
import { IBaseAddress } from '~/utils/interface/common';
import { DEFAULT_NUMBER_ZERO, EMPTY_STRING } from '~/utils/constants/common';
// Styles, images, icons
import styles from './CustomerAddress.module.scss';
import { icons } from '~/assets';

type Props = {
  index: number;
  onRemoveItem: (index: number) => void;
};

const cx = classNames.bind(styles);

const CustomerAddress = (props: Props) => {
  //#region Destructuring Props
  const { index, onRemoveItem } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/customer']);

  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<IFormCustomer>();

  const addresses = watch('addresses') || [];
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleSelectBillingAddress = (selectedIndex: number) => {
    addresses?.forEach((_, index) => {
      setValue(`addresses.${index}.isBillingAddress`, index === selectedIndex, { shouldValidate: true, shouldDirty: true });
    });
  };

  const handleSelectAddress = (value: IBaseAddress) => {
    const { address, city, state, zipCode, country, latitude, longitude } = value;

    setValue(`addresses.${index}.address`, address || EMPTY_STRING, { shouldValidate: true, shouldDirty: true });
    setValue(`addresses.${index}.city`, city || EMPTY_STRING, { shouldValidate: true, shouldDirty: true });
    setValue(`addresses.${index}.state`, state || EMPTY_STRING, { shouldValidate: true, shouldDirty: true });
    setValue(`addresses.${index}.zipCode`, zipCode || EMPTY_STRING, { shouldValidate: true, shouldDirty: true });
    setValue(`addresses.${index}.country`, country || EMPTY_STRING, { shouldValidate: true, shouldDirty: true });
    setValue(`addresses.${index}.latitude`, latitude, { shouldValidate: true, shouldDirty: true });
    setValue(`addresses.${index}.longitude`, longitude, { shouldValidate: true, shouldDirty: true });
  };
  //#endregion Handle Function

  return (
    <Grid2
      component='div'
      id='customerAddressComponent'
      className={cx('container')}
      container
      rowSpacing={'12px'}
      columnSpacing={'12px'}
    >
      <Grid2
        size={'grow'}
        container
        rowSpacing={'12px'}
        columnSpacing={'12px'}
        className={cx(index !== DEFAULT_NUMBER_ZERO ? 'item' : EMPTY_STRING)}
      >
        <Grid2 size={12}>
          <Controller
            name={`addresses.${index}.isBillingAddress`}
            key={`addresses.${index}.isBillingAddress`}
            control={control}
            render={({ field: { value } }) => (
              <FormControlLabel
                className={cx('formControlLabel')}
                slotProps={{
                  typography: {
                    className: cx('checkboxLabel'),
                  },
                }}
                control={
                  <Radio
                    checked={value}
                    onChange={() => handleSelectBillingAddress(index)}
                    name={`addresses.${index}.isBillingAddress`}
                    className={cx('radio')}
                  />
                }
                label={t('field_billing_address_label')}
              />
            )}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            name={`addresses.${index}.address`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <BaseInputAddress
                required
                label={t('field_address_label')}
                value={value}
                onChange={onChange}
                onSelectAddress={handleSelectAddress}
                errorMessage={errors?.addresses?.[index]?.address?.message}
              />
            )}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            name={`addresses.${index}.address2`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <BaseInput
                label={t('field_address_second_label')}
                value={value}
                onChange={onChange}
                errorMessage={errors?.addresses?.[index]?.address2?.message}
              />
            )}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            name={`addresses.${index}.city`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <BaseInput
                label={t('field_city_label')}
                value={value}
                onChange={onChange}
                errorMessage={errors?.addresses?.[index]?.city?.message}
                required
              />
            )}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            name={`addresses.${index}.state`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <BaseInput
                required
                label={t('field_state_label')}
                value={value}
                onChange={onChange}
                errorMessage={errors?.addresses?.[index]?.state?.message}
              />
            )}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            name={`addresses.${index}.zipCode`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <BaseInput
                label={t('field_zip_code_label')}
                value={value}
                onChange={onChange}
                errorMessage={errors?.addresses?.[index]?.zipCode?.message}
                required
              />
            )}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            name={`addresses.${index}.country`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <BaseInput
                label={t('field_country_label')}
                value={value}
                onChange={onChange}
                errorMessage={errors?.addresses?.[index]?.country?.message}
                required
              />
            )}
          />
        </Grid2>
      </Grid2>

      <Grid2 size='auto' className='flex justify-center items-center'>
        <IconButton className={cx('closeField')} onClick={() => onRemoveItem(index)}>
          <img src={icons.commonIconClose} alt={t('common_alt_img', { ns: 'common/base' })} />
        </IconButton>
      </Grid2>
    </Grid2>
  );
};

export default CustomerAddress;
